/**
 * Vision Service
 *
 * Top-level service that processes images using Google Vision API and
 * uses an AI-first approach for device information extraction.
 */

import { AnalysisContext, DeviceInfo, GoogleVisionResponse } from "./types";
import { AIDeviceAnalyzer } from "./analyzers/aiAnalyzer";
import { AIModel } from "../gemini/config";

/**
 * Vision Service class
 */
export class VisionService {
  private analyzer: AIDeviceAnalyzer;
  private aiModel: AIModel = AIModel.GEMINI;

  /**
   * Creates a new Vision Service instance
   * @param aiModel AI model to use (default: Gemini Pro)
   */
  constructor(aiModel: AIModel = AIModel.GEMINI) {
    this.aiModel = aiModel;

    // Create the AI-first analyzer
    this.analyzer = new AIDeviceAnalyzer(this.aiModel);
  }

  /**
   * Analyzes a device image and extracts device information
   * @param imageData The image data as a base64 string
   * @returns The extracted device information
   */
  async analyzeDeviceImage(imageData: string): Promise<Partial<DeviceInfo>> {
    try {
      // Check if the image is valid
      if (!imageData || typeof imageData !== 'string' || !imageData.startsWith('data:image/')) {
        throw new Error('Invalid image data format');
      }

      // Create the analysis context
      const context = await this.createAnalysisContext(imageData);

      // Get initial results (minimal placeholder)
      const initialResults = this.analyzer.analyze(context);

      try {
        // Wait for AI extraction to complete
        const enhancedResults = await this.analyzer.getEnhancedResults(context);

        return enhancedResults;
      } catch (error) {
        console.error("Error during AI extraction:", error);
        // Fall back to pattern matching if AI extraction fails
        return initialResults;
      }
    } catch (error) {
      console.error("Error analyzing device image:", error);
      return {
        confidence: 0,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Creates the analysis context for the given image data
   * @param imageData The image data as a base64 string
   * @returns The analysis context
   */
  async createAnalysisContext(imageData: string): Promise<AnalysisContext> {
    try {
      // Remove data URL prefix if present
      const imageDataWithoutPrefix = imageData.replace(
        /^data:image\/\w+;base64,/,
        ""
      );

      // Create request body
      const requestBody = {
        requests: [
          {
            image: {
              content: imageDataWithoutPrefix,
            },
            features: [
              {
                type: "TEXT_DETECTION",
                maxResults: 50,
              },
              {
                type: "LABEL_DETECTION",
                maxResults: 20,
              },
              {
                type: "LOGO_DETECTION",
                maxResults: 10,
              },
              {
                type: "WEB_DETECTION",
                maxResults: 20,
              },
            ],
          },
        ],
      };

      // Make API request
      const response = await fetch(
        "https://vision.googleapis.com/v1/images:annotate?key=" +
        process.env.REACT_APP_GOOGLE_API_KEY,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Vision API error: ${response.statusText}`);
      }

      const data: GoogleVisionResponse = await response.json();
      return this.processVisionResponse(data);
    } catch (error) {
      console.error("Error creating analysis context:", error);
      throw error;
    }
  }

  /**
   * Processes the Vision API response to extract device information
   * @param response The Google Vision API response
   * @returns The analysis context
   */
  processVisionResponse(response: GoogleVisionResponse): AnalysisContext {
    try {
      // Extract text annotations
      const textAnnotations = response.responses[0]?.textAnnotations || [];
      const allText =
        textAnnotations.length > 0 ? textAnnotations[0].description : "";

      // Extract label annotations
      const labelAnnotations = response.responses[0]?.labelAnnotations || [];

      // Extract logo annotations
      const logoAnnotations = response.responses[0]?.logoAnnotations || [];

      // Extract web entities
      const webEntities =
        response.responses[0]?.webDetection?.webEntities || [];

      // Extract best guess labels
      const bestGuessLabels =
        response.responses[0]?.webDetection?.bestGuessLabels || [];

      // Create analysis context
      const context: AnalysisContext = {
        text: allText,
        labelAnnotations,
        logoAnnotations,
        webEntities,
        bestGuessLabels,
      };

      return context;
    } catch (error) {
      console.error("Error processing vision response:", error);
      throw error;
    }
  }

  /**
   * Analyzes an image using the backend API
   * @param imageDataUrl The image data URL
   * @returns Device information extracted from the image
   */
  async analyzeImageWithBackend(imageDataUrl: string): Promise<DeviceInfo> {
    try {
      // In a real implementation, this would call your backend
      // For now, we'll just call the direct method
      const result = await this.analyzeDeviceImage(imageDataUrl);

      // Ensure we have a valid DeviceInfo object with required fields
      return {
        confidence: result.confidence || 0,
        ...result,
      };
    } catch (error) {
      console.error("Error analyzing image with backend:", error);
      return {
        confidence: 0,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}
